import { useState } from 'react';

import { ApiError } from '@solar/api-client';
import {
  AS_STATE_LABEL,
  ErrorBox,
  Field,
  Spinner,
  formatDate,
  formatDateTime,
  formatPhone,
  useAsync,
} from '@solar/ui';

import { api } from '../api';

/**
 * A/S 상세 — 상태에 따라 다음 한 가지 조치만 노출한다.
 * 미배정(1) → 기사 배정, 배정(2) → 완료 처리, 완료(3) → 조회만.
 * 상태 전이 검증은 서버가 하고(409), 여기서는 메시지만 보여준다.
 */
export function AsDetail({
  asId,
  onClose,
  onChanged,
}: {
  asId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const item = useAsync(() => api.getAs(asId), [asId]);

  if (item.loading) return <Spinner />;
  if (item.error) return <ErrorBox message={item.error} onRetry={item.reload} />;
  if (!item.data) return null;

  const a = item.data;

  function changed() {
    item.reload();
    onChanged();
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>
          A/S #{a.id} · {a.user_name ?? `주문 #${a.build_order_id}`}
        </h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel">
        <Field label="상태">
          <span className={`badge a${a.state}`}>{AS_STATE_LABEL[a.state] ?? a.state}</span>
        </Field>
        <Field label="고객">{a.user_name ?? '-'}</Field>
        <Field label="연락처">{formatPhone(a.user_phone)}</Field>
        <Field label="주소">{a.addr_full ?? '-'}</Field>
        <Field label="접수일">{formatDate(a.as_date)}</Field>
        <Field label="접수내용">{a.etc ?? '-'}</Field>
        <Field label="담당기사">{a.engineer_name ?? '미배정'}</Field>
        <Field label="처리예정일">{formatDate(a.as_due_date)}</Field>
        <Field label="완료일">{formatDate(a.com_date)}</Field>
        {a.state === 3 && <Field label="처리내용">{a.engineer_memo ?? '-'}</Field>}
      </section>

      {a.state === 1 && <AssignPanel asId={a.id} onDone={changed} />}
      {a.state === 2 && <CompletePanel asId={a.id} onDone={changed} />}

      <h3 className="section">메모</h3>
      {a.memos.length === 0 ? (
        <p className="muted">메모가 없습니다.</p>
      ) : (
        <ul className="memos">
          {a.memos.map((m) => (
            <li key={m.id}>
              <p>{m.memo}</p>
              <span className="muted">
                {m.user_name ?? '-'} · {formatDateTime(m.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** 기사 배정 — `GET /users?role=4` 검색 후 선택, 처리예정일은 선택 입력. */
function AssignPanel({ asId, onDone }: { asId: number; onDone: () => void }) {
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [engineerId, setEngineerId] = useState<number | ''>('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const engineers = useAsync(
    () => api.listUsers({ role: 4, keyword: applied, size: 50 }),
    [applied],
  );

  async function assign() {
    if (engineerId === '') return;
    setBusy(true);
    setError(null);
    try {
      await api.assignAs(asId, {
        engineer_id: engineerId,
        as_due_date: dueDate || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '배정하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3 className="section">기사 배정</h3>
      <section className="panel">
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(keyword.trim());
            setEngineerId('');
          }}
        >
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="기사 이름 검색"
          />
          <button type="submit">검색</button>
        </form>

        {engineers.loading && <Spinner label="기사 목록 불러오는 중…" />}
        {engineers.error && <ErrorBox message={engineers.error} onRetry={engineers.reload} />}
        {engineers.data && (
          <select
            value={engineerId}
            onChange={(e) => setEngineerId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">기사 선택 (총 {engineers.data.total}명)</option>
            {engineers.data.items.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ?? u.user_id}
              </option>
            ))}
          </select>
        )}

        <label className="labeled">
          처리예정일
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </label>

        {error && <p className="error">{error}</p>}
        <div className="row">
          <button
            type="button"
            className="primary"
            disabled={busy || engineerId === ''}
            onClick={() => void assign()}
          >
            {busy ? '배정 중…' : '배정'}
          </button>
        </div>
      </section>
    </>
  );
}

/** 완료 처리 — 처리 내용(engineer_memo)은 필수. */
function CompletePanel({ asId, onDone }: { asId: number; onDone: () => void }) {
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function complete() {
    setBusy(true);
    setError(null);
    try {
      await api.completeAs(asId, memo.trim());
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3 className="section">완료 처리</h3>
      <section className="panel">
        <textarea
          rows={3}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="처리 내용 (필수)"
        />
        {error && <p className="error">{error}</p>}
        <div className="row">
          <button
            type="button"
            className="primary"
            disabled={busy || memo.trim().length === 0}
            onClick={() => void complete()}
          >
            {busy ? '처리 중…' : '완료 저장'}
          </button>
        </div>
      </section>
    </>
  );
}
